'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { getSession } from 'next-auth/react';
import { RefCallback, useEffect, useRef, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import * as z from 'zod';

import { login } from '@/actions/account/login';
import { requestLoginOtp, verifyLoginOtp } from '@/actions/account/login-otp';
import { LoginOtpRequestSchema, LoginOtpVerifySchema, LoginSchema } from '@/schemas';
import { ResponseType } from '@/types/response-types';

export type LoginMode = 'password' | 'otp-request' | 'otp-verify';
type LoginMethod = 'password' | 'otp';
type LoginInputRefKind = 'passwordEmail' | 'password' | 'otpRequestEmail' | 'otpVerifyCode';

type PasswordLoginValues = z.infer<typeof LoginSchema>;
type LoginOtpRequestValues = z.infer<typeof LoginOtpRequestSchema>;
type LoginOtpVerifyValues = z.infer<typeof LoginOtpVerifySchema>;

const getOtpVerifyErrorTarget = (message: string): 'code' | 'root' => {
    const normalizedMessage = message.trim().toLowerCase();

    if (normalizedMessage.startsWith('code incorrect') || normalizedMessage.startsWith('code expired')) {
        return 'code';
    }

    return 'root';
};

type LoginSuccessResult = {
    redirectUrl: string;
    email: string;
    method: LoginMethod;
};

type UseLoginFlowOptions = {
    next?: string;
    onLoginSuccess?: (result: LoginSuccessResult) => Promise<void> | void;
    onPasswordExpired?: (email: string) => void;
    defaultEmail?: string;
    lockedEmail?: boolean;
};

export type UseLoginFlowReturn = {
    mode: LoginMode;
    infoMessage?: string;
    isBusy: boolean;
    passwordForm: UseFormReturn<PasswordLoginValues>;
    otpRequestForm: UseFormReturn<LoginOtpRequestValues>;
    otpVerifyForm: UseFormReturn<LoginOtpVerifyValues>;
    attachInputRef: (
        kind: LoginInputRefKind,
        fieldRef?: RefCallback<HTMLInputElement>
    ) => RefCallback<HTMLInputElement>;
    switchToOtpRequest: () => void;
    switchToPassword: () => void;
    switchToOtpEmail: () => void;
    submitPasswordLogin: (values: PasswordLoginValues) => Promise<void>;
    submitOtpRequest: (values: LoginOtpRequestValues, isResend?: boolean) => Promise<void>;
    submitOtpVerify: (values: LoginOtpVerifyValues) => Promise<void>;
    resendOtpCode: () => Promise<void>;
    syncPasswordAutofillOnSubmit: () => void;
    syncOtpRequestAutofillOnSubmit: () => void;
    syncOtpCodeAutofillOnSubmit: () => void;
};

export const useLoginFlow = ({
    next,
    onLoginSuccess,
    onPasswordExpired,
    defaultEmail,
    lockedEmail = false,
}: UseLoginFlowOptions = {}): UseLoginFlowReturn => {
    const [mode, setMode] = useState<LoginMode>('password');
    const [infoMessage, setInfoMessage] = useState<string>();
    const passwordEmailInputRef = useRef<HTMLInputElement | null>(null);
    const passwordInputRef = useRef<HTMLInputElement | null>(null);
    const otpRequestEmailInputRef = useRef<HTMLInputElement | null>(null);
    const otpVerifyCodeInputRef = useRef<HTMLInputElement | null>(null);

    const passwordForm = useForm<PasswordLoginValues>({
        resolver: zodResolver(LoginSchema),
        // shouldUnregister: true,
        defaultValues: {
            email: defaultEmail ?? '',
            password: '',
            next,
        },
    });

    const otpRequestForm = useForm<LoginOtpRequestValues>({
        resolver: zodResolver(LoginOtpRequestSchema),
        // shouldUnregister: true,
        defaultValues: {
            email: '',
            next,
        },
    });

    const otpVerifyForm = useForm<LoginOtpVerifyValues>({
        resolver: zodResolver(LoginOtpVerifySchema),
        // shouldUnregister: true,
        defaultValues: {
            email: '',
            code: '',
            challengeId: '',
            next,
        },
    });

    const setOtpMode = (nextMode: LoginMode) => {
        setInfoMessage(undefined);
        setMode(nextMode);
    };

    const syncPasswordEmailAutofill = () => {
        const domValue = passwordEmailInputRef.current?.value ?? '';

        if (domValue && domValue !== passwordForm.getValues('email')) {
            passwordForm.setValue('email', domValue, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    const syncPasswordAutofill = () => {
        const domValue = passwordInputRef.current?.value ?? '';

        if (domValue && domValue !== passwordForm.getValues('password')) {
            passwordForm.setValue('password', domValue, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    const syncOtpRequestEmailAutofill = () => {
        const domValue = otpRequestEmailInputRef.current?.value ?? '';

        if (domValue && domValue !== otpRequestForm.getValues('email')) {
            otpRequestForm.setValue('email', domValue, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    const syncOtpCodeAutofill = () => {
        return;
        // todo  i think this carrys over the password
        const domValue = otpVerifyCodeInputRef.current?.value ?? '';
        const digits = domValue.replace(/\D/g, '').slice(0, 6);

        if (digits && digits !== otpVerifyForm.getValues('code')) {
            otpVerifyForm.setValue('code', digits, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    useEffect(() => {
        let attemptCount = 0;
        let timeoutId: number | undefined;

        const syncAutofill = () => {
            syncPasswordEmailAutofill();
            syncPasswordAutofill();
            syncOtpRequestEmailAutofill();
            syncOtpCodeAutofill();

            attemptCount += 1;

            if (attemptCount < 10) {
                timeoutId = window.setTimeout(syncAutofill, 250);
            }
        };

        syncAutofill();

        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [mode]);

    const attachInputRef =
        (kind: LoginInputRefKind, fieldRef?: RefCallback<HTMLInputElement>): RefCallback<HTMLInputElement> =>
        (node) => {
            fieldRef?.(node);

            if (kind === 'passwordEmail') {
                passwordEmailInputRef.current = node;
                return;
            }

            if (kind === 'password') {
                passwordInputRef.current = node;
                return;
            }

            if (kind === 'otpRequestEmail') {
                otpRequestEmailInputRef.current = node;
                return;
            }

            otpVerifyCodeInputRef.current = node;
        };

    const handleLoginSuccess = async (result: LoginSuccessResult) => {
        await getSession();
        await onLoginSuccess?.(result);
    };

    const submitPasswordLogin = async (values: PasswordLoginValues) => {
        passwordForm.clearErrors('root');

        try {
            const response = await login(values);

            if (response.status === ResponseType.ERROR) {
                if (response.error === 'Account password expired') {
                    onPasswordExpired?.(values.email);
                    return;
                }

                passwordForm.setError('root', {
                    type: 'server',
                    message: response.error,
                });
                return;
            }

            await handleLoginSuccess({
                redirectUrl: response.data.redirectUrl,
                email: values.email,
                method: 'password',
            });
        } catch (error) {
            console.error('error: ', error);
            passwordForm.setError('root', {
                type: 'server',
                message: 'Something went wrong',
            });
        }
    };

    const submitOtpRequest = async (values: LoginOtpRequestValues, isResend: boolean = false) => {
        otpRequestForm.clearErrors('root');
        otpVerifyForm.clearErrors('root');
        otpVerifyForm.clearErrors('code');

        try {
            const response = await requestLoginOtp(values);

            if (response.status === ResponseType.ERROR) {
                const targetForm = mode === 'otp-verify' ? otpVerifyForm : otpRequestForm;
                targetForm.setError('root', {
                    type: 'server',
                    message: response.error,
                });
                return;
            }

            const email = response.data.sentTo;
            passwordForm.setValue('email', email);
            otpRequestForm.setValue('email', email);
            otpVerifyForm.reset({
                email,
                code: '',
                challengeId: response.data.challengeId,
                next: values.next,
            });
            setMode('otp-verify');
            setInfoMessage(`${isResend ? 'A new' : 'A'} 6-digit code was sent to ${email}.`);
        } catch (error) {
            console.error('error: ', error);
            const targetForm = mode === 'otp-verify' ? otpVerifyForm : otpRequestForm;
            targetForm.setError('root', {
                type: 'server',
                message: 'Something went wrong',
            });
        }
    };

    const submitOtpVerify = async (values: LoginOtpVerifyValues) => {
        otpVerifyForm.clearErrors('root');
        otpVerifyForm.clearErrors('code');

        try {
            const response = await verifyLoginOtp(values);

            if (response.status === ResponseType.ERROR) {
                if (getOtpVerifyErrorTarget(response.error) === 'code') {
                    otpVerifyForm.setError('code', {
                        type: 'server',
                        message: response.error,
                    });
                    return;
                }

                otpVerifyForm.setError('root', {
                    type: 'server',
                    message: response.error,
                });
                return;
            }

            await handleLoginSuccess({
                redirectUrl: response.data.redirectUrl,
                email: values.email,
                method: 'otp',
            });
        } catch (error) {
            console.error('error: ', error);
            otpVerifyForm.setError('root', {
                type: 'server',
                message: 'Something went wrong',
            });
        }
    };

    const resendOtpCode = async () => {
        await submitOtpRequest(
            {
                email: otpVerifyForm.getValues('email'),
                next: otpVerifyForm.getValues('next'),
            },
            true
        );
    };

    const switchToOtpRequest = () => {
        const email = passwordForm.getValues('email');

        otpRequestForm.reset({
            email,
            next,
        });
        otpVerifyForm.reset({
            email,
            code: '',
            challengeId: '',
            next,
        });

        if (lockedEmail) {
            submitOtpRequest(otpRequestForm.getValues());
        } else {
            setOtpMode('otp-request');
        }
    };

    const switchToPassword = () => {
        const email = otpVerifyForm.getValues('email') || otpRequestForm.getValues('email');

        passwordForm.setValue('email', email);
        setOtpMode('password');
    };

    const switchToOtpEmail = () => {
        otpVerifyForm.clearErrors('root');
        otpVerifyForm.clearErrors('code');
        otpRequestForm.reset({
            email: otpVerifyForm.getValues('email'),
            next: otpVerifyForm.getValues('next') ?? next,
        });
        setOtpMode('otp-request');
    };

    return {
        mode,
        infoMessage,
        isBusy:
            passwordForm.formState.isSubmitting ||
            otpRequestForm.formState.isSubmitting ||
            otpVerifyForm.formState.isSubmitting,
        passwordForm,
        otpRequestForm,
        otpVerifyForm,
        attachInputRef,
        switchToOtpRequest,
        switchToPassword,
        switchToOtpEmail,
        submitPasswordLogin,
        submitOtpRequest,
        submitOtpVerify,
        resendOtpCode,
        syncPasswordAutofillOnSubmit: () => {
            syncPasswordEmailAutofill();
            syncPasswordAutofill();
        },
        syncOtpRequestAutofillOnSubmit: syncOtpRequestEmailAutofill,
        syncOtpCodeAutofillOnSubmit: syncOtpCodeAutofill,
    };
};
